import { useEffect, useState } from "react";
import { AnimatePresence } from "motion/react";
import { Button, Dialog, Modal, ModalOverlay } from "react-aria-components";
import { useSelector } from "react-redux";

import { MORTGAGES_PATH } from "../../../../../config/site/ratecompare";
import { CloseIcon } from "../../../../client/assets/icons/common/CloseIcon";
import { BaseTargetedVerticalCheck } from "../../../../components/BaseComponents/BaseTargetedVerticalCheck";
import {
	FilterProvider,
	useFilter,
} from "../../../../components/Filtering/FilteringProvider";
import {
	getModalFilteringControls,
	getPageHeadline,
} from "../../../../selectors";
import { RateCompareLogo } from "../../../assets/RateCompareLogo";
import { PreviousButton } from "./helpers/PreviousButton";
import { ProgressMeter } from "./helpers/ProgressMeter";
import { StepContent } from "./helpers/StepContent";

export const MortgagesMultiStepFilteringModal = () => (
	<BaseTargetedVerticalCheck vertical={MORTGAGES_PATH}>
		<FilterProvider defaultIsOpen={true}>
			<ModalContent />
		</FilterProvider>
	</BaseTargetedVerticalCheck>
);

const ModalContent = () => {
	const pageHeadline = useSelector(getPageHeadline);
	const modalFilteringControls = useSelector(getModalFilteringControls);

	const [currentStep, setCurrentStep] = useState(0);
	const [isLastStep, setIsLastStep] = useState(false);
	const [direction, setDirection] = useState(1);

	const { isOpen, applyFilters } = useFilter();

	const handleNext = () => {
		if (currentStep < modalFilteringControls.length - 1) {
			setDirection(1);
			setCurrentStep(currentStep + 1);
		} else {
			setIsLastStep(true);
		}
	};

	const handlePrev = () => {
		if (currentStep > 0) {
			setDirection(-1);
			setCurrentStep(currentStep - 1);
		}
	};

	useEffect(() => {
		if (!isLastStep) return;

		applyFilters();
	}, [isLastStep]);

	// Measured on the first render, before react-aria locks the page scroll.
	const [scrollbarWidth] = useState(() =>
		typeof window === "undefined"
			? 0
			: window.innerWidth - document.documentElement.clientWidth,
	);

	// Pads the page by the scrollbar width while open; see `.mortgages-modal-open` in top-services.css.
	useEffect(() => {
		if (!isOpen) return;

		const html = document.documentElement;
		html.style.setProperty(
			"--mortgages-modal-scrollbar-width",
			`${scrollbarWidth}px`,
		);
		html.classList.add("mortgages-modal-open");

		return () => {
			html.classList.remove("mortgages-modal-open");
			html.style.removeProperty("--mortgages-modal-scrollbar-width");
		};
	}, [isOpen, scrollbarWidth]);

	return (
		<ModalOverlay
			isDismissable
			isOpen={isOpen}
			onOpenChange={applyFilters}
			className={({ isEntering, isExiting }) =>
				`tw:fixed tw:inset-0 tw:z-9999 tw:flex tw:min-h-full tw:origin-center tw:items-center tw:justify-center tw:overflow-y-auto tw:bg-black/75 tw:text-center tw:backdrop-blur-sm ${isEntering ? "tw:duration-200 tw:ease-out tw:animate-in tw:fade-in" : ""} ${isExiting ? "tw:duration-150 tw:ease-in tw:animate-out tw:fade-out" : ""} `
			}
		>
			<Modal
				className={({ isEntering, isExiting }) =>
					`tw:h-dvh tw:w-full tw:max-w-[640px] tw:overflow-hidden tw:bg-white tw:sm:mt-40 tw:sm:rounded-[12px] tw:md:mt-0 tw:md:h-[382px] tw:md:max-w-[800px] ${isEntering ? "tw:duration-200 tw:ease-out tw:animate-in tw:slide-in-from-bottom-full tw:md:slide-in-from-bottom-1/2" : ""} ${isExiting ? "tw:duration-150 tw:ease-in tw:animate-out tw:slide-out-to-bottom-full tw:md:slide-out-to-bottom-1/2" : ""} `
				}
			>
				<Dialog
					className="tw:relative tw:size-full tw:outline-hidden"
					aria-label="Mortgage Multi Step Filtering Modal"
				>
					{({ close }) => (
						<>
							{/* Close Button */}
							<Button
								onPress={close}
								aria-label="Dismiss"
								data-testid="mortgages-multi-step-filtering-modal-dismiss-button"
								className="tw:absolute tw:top-3.5 tw:right-2 tw:z-10 tw:inline-flex tw:cursor-pointer tw:appearance-none tw:items-center tw:rounded-full tw:border-none tw:bg-transparent tw:p-2 tw:align-middle tw:whitespace-nowrap tw:text-white tw:outline-hidden tw:transition-all tw:select-none tw:hover:bg-white/20 tw:focus-visible:ring-3 tw:focus-visible:ring-blue-500/50 tw:disabled:cursor-not-allowed tw:data-[pressed=true]:bg-white/10 tw:md:top-2 tw:md:right-2 tw:md:text-[#212121] tw:md:hover:scale-125"
							>
								<CloseIcon className="tw:text-2xl" />
							</Button>

							{/* Previous Button */}
							{currentStep > 0 && (
								<PreviousButton
									handlePrev={handlePrev}
									className="tw:hidden tw:md:block"
								/>
							)}

							{/* Modal Content */}
							<div className="tw:relative tw:size-full">
								{/* Header */}
								<div className="tw:space-y-4 tw:bg-[#0756C0] tw:px-4 tw:pt-6 tw:pb-40 tw:text-center tw:text-white tw:md:hidden">
									{/* Logo */}
									<div
										aria-label="RateCompare Logo"
										className="tw:flex tw:items-center tw:justify-center tw:gap-2"
									>
										<RateCompareLogo className="tw:text-3xl" />
										<span className="tw:align-middle tw:text-[22px] tw:leading-[30px]">
											<span className="tw:font-medium">Rate</span>
											<span className="tw:font-bold">Compare</span>
										</span>
									</div>

									{/* Page Headline */}
									<h1 className="tw:mx-auto tw:max-w-[328px] tw:text-center tw:text-3xl tw:leading-9 tw:font-bold tw:tracking-[-0.6px]">
										{pageHeadline}
									</h1>
								</div>

								{/* Steps Content */}
								<div className="tw:absolute tw:top-[186px] tw:size-full tw:overflow-hidden tw:rounded-t-[12px] tw:bg-white tw:transition-all tw:duration-300 tw:ease-in-out tw:md:relative tw:md:top-0 tw:md:p-8">
									{/* Background */}
									<div className="tw:absolute tw:top-[-650px] tw:left-1/2 tw:size-[888px] tw:-translate-x-1/2 tw:rounded-full tw:bg-[#EEF7FF] tw:sm:top-[-876px] tw:sm:size-[1110px] tw:md:top-1/2 tw:md:left-[-170px] tw:md:-translate-y-1/2" />

									{/* Progress bar */}
									<ProgressMeter
										currentStep={currentStep}
										handlePrev={handlePrev}
										totalSteps={modalFilteringControls.length}
									/>

									{/* Step Content with animation */}
									<div className="tw:relative tw:mx-auto tw:h-full tw:max-w-[410px] tw:md:max-w-none">
										<AnimatePresence
											initial={false}
											mode="popLayout"
											custom={direction}
										>
											<StepContent
												key={currentStep}
												currentStep={currentStep}
												onSelect={handleNext}
											/>
										</AnimatePresence>
									</div>
								</div>
							</div>
						</>
					)}
				</Dialog>
			</Modal>
		</ModalOverlay>
	);
};
